import { FC, useMemo, useRef, useState } from 'react';
import { langs, Stack } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { proStudioUrqlGql } from '@pypestream/api-services/urql';

import {
  FileListSection,
  useConfirmDeleteModal,
  useEditorModal,
  useFileConflictModal,
  type FileListItem,
} from '../../components';
import { useAgentManagerFiles, useProStudioViewerRole } from '../../hooks';
import {
  ACTION_FILE_ACCEPT,
  CONFIG_FILE_ACCEPT,
  isEditableActionFile,
  AgentManagerConflictError,
  buildUploadChanges,
  downloadAgentManagerFilesAsZip,
  downloadOneAgentManagerFile,
  fetchAgentManagerFileText,
  findUploadConflicts,
  syncAgentManagerFiles,
  readTypeRelativePath,
  type AgentManagerFileType,
} from '../../utils';
import { sendUserMessage } from '../../xstate/app.xstate-utils';
import { isRepositoryScaffoldingFile } from '../../utils/repository-scaffolding';

type SectionKind = 'config' | 'scripts';

const toItems = (
  files: {
    path?: string | null;
    name?: string | null;
    size?: number | null;
    mimeType?: string | null;
  }[]
): FileListItem[] =>
  files
    .filter((file) => file.path)
    .map((file) => ({
      name: file.name || file.path?.split('/').pop() || '',
      path: file.path as string,
      size: file.size,
      mimeType: file.mimeType,
    }));

// At module level so the section list keeps one reference between renders.
const canEditItem = (item: FileListItem) => isEditableActionFile(item.name);

const reportError = (error: unknown, fallback: string) =>
  sendUserMessage({
    type: 'error',
    text: error instanceof Error ? error.message : fallback,
    autoClose: 5000,
  });

/**
 * Configuration and Actions, both on the Agent Manager API. `configsFiles` are the root
 * `.py` files, `scriptsFiles` live under `services/`.
 */
export const ActionsTab: FC = () => {
  const [t] = useTranslation();
  const isViewer = useProStudioViewerRole();

  const config = useAgentManagerFiles('config');
  const scripts = useAgentManagerFiles('scripts');

  const { confirm: confirmOverwrite, fileConflictModal } =
    useFileConflictModal();
  const { confirm: confirmDelete, confirmDeleteModal } =
    useConfirmDeleteModal();

  const configInputRef = useRef<HTMLInputElement>(null);
  const scriptsInputRef = useRef<HTMLInputElement>(null);

  const [editor, setEditor] = useState<
    { path: string; name: string; code: string; kind: SectionKind } | undefined
  >();
  const [isSaving, setIsSaving] = useState(false);

  const pythonLangs = useMemo(() => [langs.python()], []);

  const sectionOf = (kind: SectionKind) =>
    kind === 'config' ? config : scripts;
  const apiTypeOf = (kind: SectionKind): AgentManagerFileType =>
    kind === 'config' ? 'config' : 'scripts';

  const saveEditor = async (code: string) => {
    if (!editor) return;
    const source = sectionOf(editor.kind);
    const { solutionId, versionName } = source;
    if (!solutionId || !versionName) return;

    setIsSaving(true);
    try {
      // The editor always writes back over the file it opened, so the path is the one it
      // was loaded from — not rebuilt from the name — and the sha is whatever the list
      // reported for it, which is what makes a concurrent edit surface as a conflict.
      await syncAgentManagerFiles({
        solutionId,
        versionName,
        commitMessage: `Edited ${editor.path}`,
        changes: buildUploadChanges({
          type: apiTypeOf(editor.kind),
          files: [
            {
              relativePath: readTypeRelativePath(
                apiTypeOf(editor.kind),
                editor.path
              ),
              file: new File([code], editor.name, { type: 'text/plain' }),
            },
          ],
          existing: source.files,
        }),
      });
      source.refetch();
      setEditor(undefined);
    } catch (error) {
      reportError(error, 'Error saving file');
    } finally {
      setIsSaving(false);
    }
  };

  const { editorModal, openModal: openEditorModal } = useEditorModal({
    loading: isSaving,
    code: editor?.code,
    title: editor?.name,
    langs: pythonLangs,
    viewOnly: isViewer,
    onSave: (code: string) => void saveEditor(code),
    onClose: () => {
      setEditor(undefined);
      setIsSaving(false);
    },
  });

  const openEditor = async (item: FileListItem, kind: SectionKind) => {
    const { solutionId, versionName } = sectionOf(kind);
    if (!solutionId || !versionName) return;
    // Defence in depth: the row's edit button, hidden for these types through `canEdit`, is
    // the only way in today (`file-list-section.tsx:152`). This keeps a future entrance from
    // rewriting a binary certificate as text. LIMITATION: no test pins this line, since the
    // package renders no React in a unit test; only the predicate and the hidden button are
    // covered.
    if (!isEditableActionFile(item.name)) return;
    try {
      const code = await fetchAgentManagerFileText({
        solutionId,
        versionName,
        path: item.path,
      });
      setEditor({ path: item.path, name: item.name, code, kind });
      openEditorModal();
    } catch (error) {
      reportError(error, `Error opening ${item.name}`);
    }
  };

  const handlePicked = async (fileList: FileList | null, kind: SectionKind) => {
    if (!fileList?.length) return;
    const source = sectionOf(kind);
    const { solutionId, versionName } = source;
    if (!solutionId || !versionName) return;

    const picked = Array.from(fileList);
    const conflicts = findUploadConflicts(
      picked.map((file) => file.name),
      source.files.map((file) => file.path || '')
    );
    if (!(await confirmOverwrite(conflicts))) return;

    try {
      await syncAgentManagerFiles({
        solutionId,
        versionName,
        commitMessage: `Uploaded ${picked.length} file(s)`,
        changes: buildUploadChanges({
          type: apiTypeOf(kind),
          files: picked.map((file) => ({ relativePath: file.name, file })),
          existing: source.files,
        }),
      });
      source.refetch();
    } catch (error) {
      // The prompt above only warns about what the client can see; the server rejects the
      // whole batch if anything changed underneath, and then the list is out of date.
      if (error instanceof AgentManagerConflictError) source.refetch();
      reportError(error, 'Error uploading files');
    }
  };

  const handleDelete = async (item: FileListItem, kind: SectionKind) => {
    const source = sectionOf(kind);
    const { solutionId, versionName } = source;
    if (!solutionId || !versionName) return;
    if (!(await confirmDelete(item.name))) return;

    try {
      const response = await proStudioUrqlGql.deleteAgentManagerFile({
        filePath: item.path,
        solutionId,
        versionName,
      });
      if (response.error) throw new Error(response.error.message);
      source.refetch();
    } catch (error) {
      reportError(error, `Error deleting ${item.name}`);
    }
  };

  const handleDownloadAll = async (kind: SectionKind) => {
    const source = sectionOf(kind);
    const { solutionId, versionName } = source;
    if (!solutionId || !versionName) return;
    try {
      await downloadAgentManagerFilesAsZip({
        solutionId,
        versionName,
        paths: source.files.map((file) => file.path || '').filter(Boolean),
        zipName: `${versionName}-${kind}`,
      });
    } catch (error) {
      reportError(error, 'Error downloading files');
    }
  };

  const handleDownloadOne = async (item: FileListItem, kind: SectionKind) => {
    const { solutionId, versionName } = sectionOf(kind);
    if (!solutionId || !versionName) return;
    try {
      await downloadOneAgentManagerFile({
        solutionId,
        versionName,
        path: item.path,
        fileName: item.name,
      });
    } catch (error) {
      reportError(error, `Error downloading ${item.name}`);
    }
  };

  const sections: {
    kind: SectionKind;
    testIdPrefix: string;
    inputRef: React.RefObject<HTMLInputElement>;
    accept: string;
    title: string;
    tooltip: string;
    uploadLabel: string;
    showSearch: boolean;
    showDownloadAll: boolean;
  }[] = [
    {
      kind: 'config',
      testIdPrefix: 'ps-configuration',
      inputRef: configInputRef,
      accept: CONFIG_FILE_ACCEPT,
      title:
        t('pro-studio/actions:section1.title', {
          defaultValue: 'Configuration',
        }) ?? '',
      tooltip:
        t('pro-studio/actions:subtitle.part1', {
          defaultValue:
            'Config files uploaded below must have a .py extension.',
        }) ?? '',
      uploadLabel:
        t('pro-studio/actions:section1.action', {
          defaultValue: 'Upload Config',
        }) ?? '',
      showSearch: false,
      showDownloadAll: false,
    },
    {
      kind: 'scripts',
      testIdPrefix: 'ps-actions',
      inputRef: scriptsInputRef,
      accept: ACTION_FILE_ACCEPT,
      title:
        t('pro-studio/actions:section2.title', { defaultValue: 'Actions' }) ??
        '',
      tooltip:
        t('pro-studio/actions:subtitle.part1', {
          defaultValue:
            'Actions allow the designer to perform custom integrations and special logic.',
        }) ?? '',
      uploadLabel:
        t('pro-studio/actions:section2.action', {
          defaultValue: 'Upload Actions',
        }) ?? '',
      showSearch: true,
      showDownloadAll: true,
    },
  ];

  return (
    <>
      {/*
        24px between the cards, owned here rather than left to PageSection's own
        margin — which collapses to nothing between two adjacent sections.
      */}
      <Stack direction="column" gutter="large" alignItems="stretch">
        {sections.map((section) => {
          const source = sectionOf(section.kind);
          return (
            <div key={section.kind}>
              <input
                ref={section.inputRef}
                type="file"
                hidden
                multiple
                accept={section.accept}
                onChange={(event) => {
                  void handlePicked(event.target.files, section.kind);
                  // Allow re-picking the same file after a cancelled overwrite.
                  event.target.value = '';
                }}
              />
              <FileListSection
                testIdPrefix={section.testIdPrefix}
                title={section.title}
                tooltip={section.tooltip}
                items={toItems(
                  // Display only: `source.files` still holds them, so an upload of the
                  // same name stays a sha-checked update instead of a conflicting add.
                  section.kind === 'config'
                    ? source.files.filter(
                        (file) => !isRepositoryScaffoldingFile(file.path ?? '')
                      )
                    : source.files
                )}
                readOnly={isViewer}
                uploadDisabled={!source.versionName}
                uploadLabel={section.uploadLabel}
                rowActions={['edit', 'download', 'delete']}
                canEdit={canEditItem}
                showSearch={section.showSearch}
                showDownloadAll={section.showDownloadAll}
                isLoading={source.isLoading}
                error={source.error}
                emptyLabel={
                  t('pro-studio/actions:file_list.empty', {
                    defaultValue: 'No files yet.',
                  }) ?? ''
                }
                onUploadClick={() => section.inputRef.current?.click()}
                onDownloadAll={() => void handleDownloadAll(section.kind)}
                onEdit={(item) => void openEditor(item, section.kind)}
                onDownload={(item) =>
                  void handleDownloadOne(item, section.kind)
                }
                onDelete={(item) => void handleDelete(item, section.kind)}
              />
            </div>
          );
        })}
      </Stack>
      {fileConflictModal}
      {confirmDeleteModal}
      {editorModal}
    </>
  );
};
